import React, { useEffect, useRef, useState } from 'react';
import Plyr from 'plyr';
import IconNext from './icons/IconNext';
import IconClose from './icons/IconClose';
import 'plyr/dist/plyr.css';
import styles from './AudioPlayer.module.scss';
import { useAudioContext } from './AudioContext';

const AudioPlayer = ({
  selectedAudio,
  handleCloseAudio,
  handleNextAudio,
  songName,
}) => {
  const { currentTimeRef } = useAudioContext();
  const playerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Keep latest callback in a ref so the setup effect below doesn't depend on
  // handler identity. Otherwise any parent re-render (e.g. Realtime DB
  // playCounts update from another device) would destroy + recreate Plyr and
  // restart the current song from 0.
  const handleNextAudioRef = useRef(handleNextAudio);
  useEffect(() => {
    handleNextAudioRef.current = handleNextAudio;
  }, [handleNextAudio]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  // Watch for changes in the songName prop
  useEffect(() => {
    if (playerRef.current) {
      // Update the player's title when songName changes
      playerRef.current.title = songName;
    }
  }, [songName]);

  useEffect(() => {
    if (selectedAudio) {
      if (playerRef.current) {
        playerRef.current.destroy();
      }

      playerRef.current = new Plyr('#player', {
        controls: ['play-large', 'play', 'progress'],
        autoplay: true,
        volume: 1,
      });

      playerRef.current.on('timeupdate', () => {
        const t = playerRef.current.currentTime;
        setCurrentTime(t);
        currentTimeRef.current = t;
      });

      playerRef.current.on('loadedmetadata', (event) => {
        setDuration(playerRef.current.duration);
      });

      playerRef.current.on('ended', () => handleNextAudioRef.current?.());

      playerRef.current.source = {
        type: 'audio',
        sources: [
          {
            src: selectedAudio,
            type: 'audio/mp3',
          },
        ],
      };
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [selectedAudio]);

  return (
    <div className={styles.Player}>
      {selectedAudio && (
        <div className={styles.playercontainer}>
          <div className={styles.playerHeader}>
            <div className={styles.left}>
              <p>now playing:</p>
              <h3>{songName}</h3>
            </div>
            <div className={styles.right}>
              <button onClick={() => handleCloseAudio(false)}>
                <IconClose color='#FDE68A' />
              </button>
            </div>
          </div>
          <div className={styles.playerInner}>
            <div className={styles.left}>
              <audio id='player' controls>
                <source src={selectedAudio} type='audio/mp3' />
                Your browser does not support the audio element.
              </audio>
              <span className={styles.time}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>
            <div className={styles.right}>
              <button onClick={handleNextAudio}>
                <IconNext color='#FDE68A' />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(AudioPlayer);
